import React, { useContext, useEffect, useState } from 'react';
import { AuthContext } from './AuthContext';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

const DomainGuard = ({ children, accessType, requiredAccess }) => {
  const { user, currentDomain, logout } = useContext(AuthContext);
  const [isAuthorized, setIsAuthorized] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const verifyAccess = async () => {
      // SuperAdmin bypass all domain checks
      if (user?.role === 'SuperAdmin') {
        console.log('SuperAdmin access granted - bypassing domain checks');
        setIsAuthorized(true);
        setIsLoading(false);
        return;
      }
      
      // Special handling for Admin role with missing company data
      if (user?.role === 'Admin' && (!user.user.company || typeof user.user.company === 'string')) {
        console.log('Admin with incomplete company data - attempting to fetch domain access');
        
        try {
          // Create API instance
          const api = axios.create({
            baseURL: process.env.REACT_APP_API_BASE_URL,
            timeout: 10000,
            withCredentials: true,
          });
          
          // Add token to headers
          const token = localStorage.getItem('token');
          if (token) {
            api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
          }

          // Try to find company with matching domain for this admin
          const companiesResponse = await api.get('/companies');
          const companies = companiesResponse.data;
          
          const hasMatchingDomain = companies.some(company => {
            if (!company.domains) return false;
            return company.domains.some(d => {
              return d.domainName === currentDomain || 
                     currentDomain.includes(d.domainName) || 
                     d.domainName.includes(currentDomain);
            });
          });
          
          if (hasMatchingDomain) {
            console.log(`Admin access granted to domain ${currentDomain} based on company domain match`);
            setIsAuthorized(true);
          } else {
            console.warn(`No matching domain found for admin in any company: ${currentDomain}`);
            setIsAuthorized(false);
          }
        } catch (error) {
          console.error('Error verifying admin domain access:', error);
          setIsAuthorized(false);
        } finally {
          setIsLoading(false);
        }
        return;
      }

      // Regular domain verification for other cases
      try {
        // Create API instance
        const api = axios.create({
          baseURL: process.env.REACT_APP_API_BASE_URL,
          timeout: 10000,
          withCredentials: true,
        });
        
        // Add token to headers
        const token = localStorage.getItem('token');
        if (token) {
          api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
        }

        console.log(`Verifying domain access for user: ${user?.user?._id}, role: ${user?.role}, domain: ${currentDomain}`);
        
        // Verify domain access using the backend endpoint
        const response = await api.get('/companies/verify-domain-access', {
          params: { 
            userId: user?.user?._id, 
            role: user?.role,
            domain:  currentDomain
          }
        });

        if (response.data.hasAccess) {
          console.log(`Access granted to ${requiredAccess || currentDomain}`);
          setIsAuthorized(true);
        } else {
          console.warn(`Access denied for ${user?.role} to domain ${requiredAccess || currentDomain}`);
          setIsAuthorized(false);
        }
      } catch (error) {
        console.error('Error verifying domain access:', error);
        setIsAuthorized(false);
      } finally {
        setIsLoading(false);
      }
    };

    if (user) {
      verifyAccess();
    } else {
      setIsLoading(false);
      setIsAuthorized(false);
    }
  }, [user, currentDomain, requiredAccess, accessType]);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-indigo-600 via-purple-700 to-pink-600 flex items-center justify-center p-4 relative overflow-hidden">
        {/* Animated background elements */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute w-96 h-96 bg-white/10 rounded-full blur-3xl -top-48 -left-48 animate-pulse"></div>
          <div className="absolute w-96 h-96 bg-purple-500/20 rounded-full blur-3xl -bottom-48 -right-48 animate-pulse" style={{animationDelay: '1000ms'}}></div>
          <div className="absolute w-64 h-64 bg-pink-500/20 rounded-full blur-3xl top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 animate-pulse" style={{animationDelay: '500ms'}}></div>
        </div>

        {/* Loading Card */}
        <div className="bg-white/95 backdrop-blur-md rounded-3xl shadow-2xl p-12 w-full max-w-md border border-white/30 relative z-20 text-center">
          {/* Logo */}
          <div className="flex justify-center mb-6">
            <div className="bg-gradient-to-br from-indigo-500 to-purple-600 rounded-3xl p-6 shadow-xl">
              <img 
                src="/sanibiz.png"  
                alt="SaniBiz Logo" 
                className="w-20 h-20 rounded-2xl"
              />
            </div>
          </div>

          {/* Animated Spinner */}
          <div className="flex justify-center mb-6">
            <div className="relative">
              <div className="animate-spin rounded-full h-16 w-16 border-4 border-indigo-200"></div>
              <div className="absolute top-0 left-0 animate-spin rounded-full h-16 w-16 border-4 border-transparent border-t-indigo-600 border-r-purple-600"></div>
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="w-8 h-8 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-full animate-pulse"></div>
              </div>
            </div>
          </div>

          {/* Loading Text */}
          <h2 className="text-2xl font-bold bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 bg-clip-text text-transparent mb-3">
            Verifying Access
          </h2>
          <p className="text-gray-600 font-medium mb-4">
            Please wait while we verify your domain access...
          </p>

          {/* Progress Dots */}
          <div className="flex justify-center gap-2">
            <div className="w-2 h-2 bg-indigo-600 rounded-full animate-bounce"></div>
            <div className="w-2 h-2 bg-purple-600 rounded-full animate-bounce" style={{animationDelay: '150ms'}}></div>
            <div className="w-2 h-2 bg-pink-600 rounded-full animate-bounce" style={{animationDelay: '300ms'}}></div>
          </div>
        </div>
      </div>
    );
  }

  if (!isAuthorized) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-red-600 via-pink-700 to-rose-600 flex items-center justify-center p-4 relative overflow-hidden">
        {/* Animated background elements */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute w-96 h-96 bg-white/10 rounded-full blur-3xl -top-48 -left-48 animate-pulse"></div>
          <div className="absolute w-96 h-96 bg-rose-500/20 rounded-full blur-3xl -bottom-48 -right-48 animate-pulse" style={{animationDelay: '1000ms'}}></div>
          <div className="absolute w-64 h-64 bg-pink-500/20 rounded-full blur-3xl top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 animate-pulse" style={{animationDelay: '500ms'}}></div>
        </div>

        {/* Access Denied Card */}
        <div className="bg-white/95 backdrop-blur-md rounded-3xl shadow-2xl p-12 w-full max-w-md border border-white/30 relative z-20 text-center">
          {/* Error Icon */}
          <div className="flex justify-center mb-6">
            <div className="bg-gradient-to-br from-red-500 to-pink-600 rounded-full p-6 shadow-xl">
              <svg className="w-16 h-16 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
            </div>
          </div>

          {/* Error Title */}
          <h2 className="text-3xl font-bold bg-gradient-to-r from-red-600 via-pink-600 to-rose-600 bg-clip-text text-transparent mb-4">
            Domain Access Denied
          </h2>

          {/* Error Message */}
          <div className="bg-red-50 border-2 border-red-200 rounded-xl p-6 mb-6">
            <p className="text-red-700 font-semibold mb-3">
              You don't have access to this domain:
            </p>
            <div className="bg-white rounded-lg px-4 py-3 mb-3">
              <p className="text-red-800 font-bold text-lg break-all">
                {currentDomain}
              </p>
            </div>
            <p className="text-red-600 text-sm">
              Please contact your administrator to request access.
            </p>
          </div>

          {/* Action Buttons */}
          <div className="flex flex-col gap-3">
            <button
              onClick={() => {
                logout();
                navigate('/');
              }}
              className="w-full py-4 rounded-xl text-base font-bold text-white bg-gradient-to-r from-red-600 via-pink-600 to-rose-600 hover:from-red-700 hover:via-pink-700 hover:to-rose-700 focus:outline-none focus:ring-4 focus:ring-red-300 shadow-xl hover:shadow-2xl transition-all duration-300 transform hover:scale-105 active:scale-95"
            >
              <span className="flex items-center justify-center gap-2">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                </svg>
                Logout & Return to Login
              </span>
            </button>
            
            <button
              onClick={() => window.location.reload()}
              className="w-full py-3 rounded-xl text-sm font-semibold text-gray-700 bg-white border-2 border-gray-300 hover:border-gray-400 hover:bg-gray-50 focus:outline-none focus:ring-4 focus:ring-gray-200 transition-all duration-300"
            >
              <span className="flex items-center justify-center gap-2">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
                Try Again
              </span>
            </button>
          </div>
        </div>
      </div>
    );
  }

  return <>{children}</>;
};

export default DomainGuard;